// ============================================================
// Client offboarding
//
// "Start offboarding" on a client creates two ordinary tasks with checklists:
// one for the owner (billing and admin) and one for the client's Account
// Director (accounts, access, the goodbye, ads). The owner's last step is
// "Finish and archive", which opens the same archive questions as before and
// warns, hard to get past, if the AD's checklist is not finished.
//
// Tasks are marked with tasks.offboarding = 'admin' | 'ad' (see
// supabase/migrations/offboarding.sql) so they can be found again reliably.
// ============================================================
import { useState, useEffect } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { TERMINATION_REASONS, DONE_STATUSES } from './constants'

const todayLocal = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const item = (text) => ({ text, checked: false })
const header = (text) => ({ text: `## ${text}`, checked: false })

/** The owner's checklist. Commissions are not on it: archiving ends them. */
export function adminChecklist() {
  return [
    item('Cancel invoices'),
    item('Remove from client list (Lauren\'s copy)'),
    item('Move Canva palette to Terminated Clients'),
  ]
}

/** The AD's checklist. Steps for services the client never had are left out. */
export function adChecklist(client) {
  const list = [
    header('Admin'),
    item('Alert the team'),
    item('Cancel upcoming meetings'),
    item('Gather all client documents from every team member (anything not already in the app, including Canva links) and add them to the client\'s Documents'),
    header('Accounts'),
    ...(client.has_social ? [item('Remove from ContentStudio')] : []),
    item('Turn off zaps'),
    item('Remove from Ahrefs'),
    header('Client access'),
    item('Send the client all passwords on file'),
    item('Make sure they have access to, and own, their WP Engine account'),
    header('Customer service'),
    item('If it ended on good terms (they thanked us, love the work but can\'t afford it, or aren\'t growing the business), ask for a review'),
  ]
  if (client.has_ads) {
    list.push(
      header('Ads'),
      item('Turn off ads'),
      item('Remove their access from our ad account'),
      item('If they want to run their own ads, export our keywords and headlines with the data so they can rebuild it in their own account'),
    )
  }
  return list
}

const realItems = (items) => (items || []).filter(it => !(typeof it?.text === 'string' && it.text.trim().startsWith('## ')))
export const checklistProgress = (task) => {
  const items = realItems(task.checklist_items)
  return { done: items.filter(it => it.checked).length, total: items.length }
}
export const isTaskDone = (task) => DONE_STATUSES.includes(task.status)

// ── Start offboarding ─────────────────────────────────────────────────────
export function StartOffboardingModal({ client, profiles, currentUser, onClose, onStarted }) {
  const toast = useToast()
  const people = (profiles || [])
    .filter(p => p.is_active !== false && !p.archived_at)
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
  const [lastDay, setLastDay] = useState(todayLocal())
  const [adminId, setAdminId] = useState(currentUser?.id || '')
  const [adId, setAdId] = useState(client.account_director_id || '')
  const [saving, setSaving] = useState(false)

  const start = async () => {
    setSaving(true)
    const base = {
      client_id: client.id,
      assigned_by: currentUser.id,
      service_area: 'account_management',
      approval_path: 'none',
      date_due: lastDay || null,
      status: 'not_started',
      // Internal housekeeping, never something the client should see.
      client_visible: false,
      is_onboarding_task: false,
      collaborator_ids: [],
      deliverable_links: [],
      google_doc_links: [],
      loom_links: [],
    }
    const rows = [
      {
        ...base,
        title: `Offboard ${client.name}: billing and admin`,
        assigned_to: adminId || null,
        offboarding: 'admin',
        notes: 'When these are done, use "Finish and archive" at the bottom of the checklist. Archiving also ends commissions.',
        checklist_items: adminChecklist(),
      },
      {
        ...base,
        title: `Offboard ${client.name}`,
        assigned_to: adId || null,
        offboarding: 'ad',
        notes: 'The client is not archived until this checklist is finished.',
        checklist_items: adChecklist(client),
      },
    ]
    const { data, error } = await supabase.from('tasks').insert(rows).select()
    if (error) {
      toast('Could not start offboarding: ' + error.message, 'error')
      setSaving(false)
      return
    }
    for (const t of data || []) {
      supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: t.id } })
      // Tell whoever got a checklist, unless it's the person who started it.
      if (t.assigned_to && t.assigned_to !== currentUser.id) {
        await supabase.from('notifications').insert({
          user_id: t.assigned_to,
          task_id: t.id,
          type: 'assigned',
          message: `${client.name} is leaving. Your offboarding checklist is ready.`,
        })
      }
    }
    toast(`Offboarding started for ${client.name}`, 'success')
    setSaving(false)
    onStarted()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{ zIndex: 100 }}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">Start offboarding {client.name}?</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            Creates two checklists. Nothing is archived yet: that's the last step of your checklist.
          </p>
          <div className="form-group" style={{ marginTop: 14 }}>
            <label className="form-label">Last day <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-control" type="date" value={lastDay} onChange={e => setLastDay(e.target.value)} style={{ maxWidth: 200 }} />
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5 }}>
              Both checklists are due this day, and it's filled in as the termination date when you archive.
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Billing and admin checklist</label>
              <select className="form-control" value={adminId} onChange={e => setAdminId(e.target.value)}>
                <option value="">Not assigned</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Account Director checklist <span style={{ color: 'var(--red)' }}>*</span></label>
              <select className="form-control" value={adId} onChange={e => setAdId(e.target.value)}>
                <option value="">— Pick the AD —</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {client.has_ads ? 'Includes the ads steps.' : 'No ads steps: this client has no ads service.'}{' '}
            {client.has_social ? 'Includes ContentStudio.' : 'No ContentStudio step: no social service.'}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm" style={{ background: 'var(--navy)', color: '#fff', border: 'none' }}
            onClick={start} disabled={saving || !lastDay || !adId}
            title={!adId ? 'Pick the Account Director' : undefined}>
            {saving ? 'Starting...' : 'Start offboarding'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Finish and archive ────────────────────────────────────────────────────
/**
 * The only way a client gets archived: from the client profile, or from the
 * "Finish and archive" button on the owner's offboarding checklist.
 *
 * If the AD's offboarding checklist is still open it says so, and archiving
 * anyway takes a deliberate tick plus typing the client's name. The AD's
 * unfinished checklist is then left open so it stays on their list.
 */
export function ArchiveClientModal({ clientId, defaultTermDate, onClose, onArchived }) {
  const toast = useToast()
  const [client, setClient] = useState(null)
  const [adTasks, setAdTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [termDate, setTermDate] = useState(defaultTermDate || todayLocal())
  const [termReason, setTermReason] = useState('')
  const [termNote, setTermNote] = useState('')
  const [bypassTicked, setBypassTicked] = useState(false)
  const [typedName, setTypedName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const [{ data: c }, { data: ts }, { data: prof }] = await Promise.all([
        supabase.from('clients').select('*').eq('id', clientId).single(),
        supabase.from('tasks').select('id, title, status, checklist_items, assigned_to, date_due')
          .eq('client_id', clientId).eq('offboarding', 'ad').is('archived_at', null),
        supabase.from('profiles').select('id, full_name'),
      ])
      if (cancelled) return
      setClient(c)
      setAdTasks((ts || []).map(t => ({ ...t, assignee: (prof || []).find(p => p.id === t.assigned_to)?.full_name })))
      // The last day set when offboarding started is the natural termination date.
      if (!defaultTermDate) {
        const due = (ts || []).map(t => t.date_due).filter(Boolean).sort()[0]
        if (due) setTermDate(due)
      }
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [clientId, defaultTermDate])

  const unfinished = adTasks.filter(t => !isTaskDone(t))
  const needsBypass = unfinished.length > 0
  const nameMatches = client && typedName.trim().toLowerCase() === (client.name || '').trim().toLowerCase()
  const bypassOk = !needsBypass || (bypassTicked && nameMatches)
  const canArchive = !loading && client && termDate && termReason && bypassOk && !saving

  const archive = async () => {
    setSaving(true)
    const now = new Date().toISOString()
    const { error } = await supabase.from('clients').update({
      archived_at: now,
      terminated_on: termDate,
      termination_reason: termReason,
      termination_note: termNote.trim() || null,
    }).eq('id', clientId)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }

    // The owner's checklist is finished by this.
    await supabase.from('tasks').update({ status: 'live' })
      .eq('client_id', clientId).eq('offboarding', 'admin').is('archived_at', null)
      .not('status', 'in', `(${DONE_STATUSES.join(',')})`)

    // Archive every task on the client, except an unfinished AD checklist:
    // archiving that would hide it from the AD before the work is done.
    let q = supabase.from('tasks').update({ archived_at: now }).eq('client_id', clientId).is('archived_at', null)
    if (unfinished.length) q = q.not('id', 'in', `(${unfinished.map(t => t.id).join(',')})`)
    const { error: tErr } = await q
    if (tErr) { toast('Client archived, but task cascade failed: ' + tErr.message, 'error'); setSaving(false); return }

    toast(needsBypass ? `${client.name} archived. The AD's checklist is still open on their list.` : `${client.name} archived`, 'success')
    setSaving(false)
    onArchived()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{ zIndex: 100 }}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <span className="modal-title">Archive {client?.name || 'client'}?</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {loading ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Checking offboarding…</p>
          ) : (
            <>
              {needsBypass && (
                <div style={{ background: '#fdf0f0', border: '1.5px solid #f0c4c4', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <AlertTriangle size={14} /> The AD's offboarding isn't finished
                  </div>
                  {unfinished.map(t => {
                    const { done, total } = checklistProgress(t)
                    const left = realItems(t.checklist_items).filter(it => !it.checked)
                    return (
                      <div key={t.id} style={{ fontSize: 12.5, color: '#8a3a3a', lineHeight: 1.6 }}>
                        <strong>{t.assignee || 'Unassigned'}</strong>: {done} of {total} done. Still open:
                        <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>
                          {left.slice(0, 6).map((it, i) => <li key={i}>{it.text}</li>)}
                          {left.length > 6 && <li>and {left.length - 6} more</li>}
                        </ul>
                      </div>
                    )
                  })}
                  <div style={{ borderTop: '1px solid #f0c4c4', marginTop: 10, paddingTop: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#8a3a3a', cursor: 'pointer' }}>
                      <input type="checkbox" checked={bypassTicked} onChange={e => { setBypassTicked(e.target.checked); setTypedName('') }} style={{ marginTop: 3 }} />
                      <span>Archive anyway. The AD's checklist stays open on their list until they finish it.</span>
                    </label>
                    {bypassTicked && (
                      <div style={{ marginTop: 8 }}>
                        <label className="form-label" style={{ color: '#8a3a3a' }}>Type <strong>{client.name}</strong> to confirm</label>
                        <input className="form-control" value={typedName} onChange={e => setTypedName(e.target.value)}
                          autoComplete="off" spellCheck={false}
                          onPaste={e => e.preventDefault()} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                <strong>{client?.name}</strong> will be hidden from active client lists, the sidebar, and the dashboard.
                Their tasks and history will be preserved, and commissions end on the termination date. You can unarchive them anytime.
              </p>

              <div className="form-group" style={{ marginTop: 14 }}>
                <label className="form-label">Termination date <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className="form-control" type="date" value={termDate} onChange={e => setTermDate(e.target.value)} style={{ maxWidth: 200 }} />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
                  The day they actually terminated, not today's date if you're catching up. Used for turnover and commission (no commission for the month they terminate).
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Why are they leaving? <span style={{ color: 'var(--red)' }}>*</span></label>
                <select className="form-control" value={termReason} onChange={e => setTermReason(e.target.value)}>
                  <option value="">— Select a reason —</option>
                  {['controllable', 'structural', 'not_churn', 'unknown'].map(g => (
                    <optgroup key={g} label={
                      g === 'controllable' ? 'Something we could have changed'
                      : g === 'structural' ? 'Outside our control'
                      : g === 'not_churn' ? 'Not really churn'
                      : 'Unknown'}>
                      {TERMINATION_REASONS.filter(r => r.group === g).map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {termReason && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
                    {TERMINATION_REASONS.find(r => r.value === termReason)?.hint}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  What did they actually say? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional but valuable)</span>
                </label>
                <textarea className="form-control" rows={2} value={termNote}
                  onChange={e => setTermNote(e.target.value)}
                  placeholder="Their words, not the category. This is what makes the pattern visible later."
                  style={{ fontSize: 12.5, resize: 'vertical' }} />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm"
            style={{ background: needsBypass ? 'var(--red)' : '#c4962a', color: '#fff', border: 'none' }}
            onClick={archive} disabled={!canArchive}
            title={!termDate ? 'Add the termination date' : !termReason ? 'Pick a reason first' : !bypassOk ? 'The AD\'s offboarding isn\'t finished' : undefined}>
            {saving ? 'Archiving...' : needsBypass ? 'Archive anyway' : 'Archive Client'}
          </button>
        </div>
      </div>
    </div>
  )
}
